// "Re-run" for a finished batch — shared by the Exe and Extension Running tabs.
//
// A re-run is deliberately NOT "run everything again". It executes only:
//   • cases a developer marked fixed  → must be verified
//   • cases that are still failing    → worth another attempt
// and skips cases that already passed untouched. Re-testing a known-good case
// costs real time and tokens, so the preview shows exactly what will run and
// what's being skipped before anything is queued.
//
// When `devicesApi` is provided (the Exe flow), the modal ALSO lets the tester
// name the re-run and pick which PCs run it — so the work is dispatched to a
// machine that's actually online this time, instead of silently re-queuing onto
// the batch's original (now-offline) PCs.
//
// Layout deliberately mirrors ExeRunner's first-run modal (RunTestCasesModal):
// same size, same name field, same section headers with a count pill and a
// select-all link, same PC list with free/busy state, same footer summary. The
// two are the same decision made twice — a tester shouldn't have to re-learn the
// screen because it's the second run. Only the middle section differs, because a
// re-run picks its own cases (the preview) instead of offering a list to tick.
import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCw, Loader2, CheckCircle2, Clock, XCircle, SkipForward, AlertTriangle, Server } from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { toastSuccess } from "@/lib/toast";

// Same field styling as the first-run modal (ExeRunner's FIELD) — kept local, as
// every other screen in this app does, rather than reaching into a page module.
const FIELD =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500";

// PC display name, matching the first-run modal — "QA laptop (DESKTOP-VKL2N4P)".
const devName = (d) =>
  d?.display_name ? `${d.display_name} (${d.label || d.id.slice(0, 8)})` : (d?.label || d?.id?.slice(0, 8) || "—");

// A PC mid-batch. It will take new work, but only after it has drained the batch
// it is on — so pre-selecting it silently turns "re-run now" into "re-run later".
const isBusy = (d) => !!d.current_run_id;

// Default PC selection. Same rule as the first-run modal — a busy PC is a
// deliberate choice, never a default — but applied to the batch's ORIGINAL
// machines first: a re-run belongs on the bench that ran it, while it is up.
// Falls back rather than returning nothing, so the tester is never left with an
// empty selection and a disabled button.
const defaultPicked = (all, originalIds) => {
  const online = all.filter((d) => d.status === "online");
  const originals = online.filter((d) => originalIds.includes(d.id));
  const pool = originals.length ? originals : online;
  const free = pool.filter((d) => !isBusy(d));
  return (free.length ? free : pool).map((d) => d.id);
};

export default function RerunBatchButton({
  batch,          // the batch object (needs id, status, rerun_count, name, device_ids)
  getPreview,     // (id) => Promise<{ will_run, will_skip, dev_fixed_to_verify, ... }>
  rerun,          // (id, body?) => Promise<{ requeued, skipped, rerun_count }>
  onDone,         // called after a successful re-run so the caller can refresh
  devicesApi,     // optional () => Promise<Device[]> — enables the name + PC picker
  size = "sm",
  variant = "outline",
}) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Picker state (only used when devicesApi is provided).
  const [devices, setDevices] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [name, setName] = useState("");
  // The parent BatchCard re-renders every 2s (live poll), which would otherwise
  // re-run the open-effect and reset the picker/name mid-edit. This guard makes
  // the modal set up exactly ONCE per open.
  const setupRef = useRef(false);

  const withPicker = typeof devicesApi === "function";

  // A re-run only makes sense once the batch has actually stopped.
  const terminal = ["completed", "stopped"].includes(String(batch?.status || "").toLowerCase());

  const loadPreview = useCallback(async () => {
    if (!batch?.id) return;
    setLoading(true); setError(null); setPreview(null);
    try {
      setPreview(await getPreview(batch.id));
    } catch (e) {
      setError((e && e.message) || "Couldn't work out what needs re-running.");
    } finally {
      setLoading(false);
    }
  }, [batch?.id, getPreview]);

  const loadDevices = useCallback(async () => {
    if (!withPicker) return;
    try {
      const list = await devicesApi();
      const arr = Array.isArray(list) ? list : [];
      setDevices(arr);
      setPicked(new Set(defaultPicked(arr, batch?.device_ids || [])));
    } catch (e) {
      setDevices([]);
    }
    // Intentionally NOT depending on batch.device_ids: it's a new array on every
    // 2s poll, which would otherwise churn this callback and reset the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withPicker, devicesApi]);

  // Set the modal up ONCE per open (guarded), so the parent's 2s poll can't
  // reset the picker/name while the tester is choosing PCs.
  useEffect(() => {
    if (!open) { setupRef.current = false; return; }
    if (setupRef.current) return;
    setupRef.current = true;
    loadPreview();
    loadDevices();
    const base = (batch?.name || "Batch").replace(/\s*—\s*re-run\s*#\d+$/i, "");
    setName(`${base} — re-run #${(batch?.rerun_count || 0) + 1}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Only online PCs are offered, exactly as in the first-run modal — an offline
  // machine is not a choice, and the server drops it from device_ids anyway. The
  // originals that went offline are reported separately below, so re-targeting is
  // still an informed decision rather than a silent one.
  const onlineDevices = devices.filter((d) => d.status === "online");
  const freeDevices = onlineDevices.filter((d) => !isBusy(d));
  const busyCount = onlineDevices.length - freeDevices.length;
  const noOnline = withPicker && onlineDevices.length === 0;
  const pickedOnline = [...picked].filter((id) => onlineDevices.some((d) => d.id === id));
  const allDevices = onlineDevices.length > 0 && pickedOnline.length === onlineDevices.length;
  // PCs that ran the batch originally but aren't up now — the reason a re-run can
  // land somewhere else, said before the tester presses the button.
  const offlineOriginals = (batch?.device_ids || [])
    .filter((id) => !onlineDevices.some((d) => d.id === id)).length;

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const canRun = !busy && !loading && !!preview?.will_run && (!withPicker || pickedOnline.length > 0);

  async function confirm() {
    setBusy(true); setError(null);
    try {
      const body = withPicker ? { device_ids: pickedOnline, name: name.trim() || undefined } : undefined;
      const res = await rerun(batch.id, body);
      setOpen(false);
      toastSuccess(
        `Re-run started — ${res.requeued} case${res.requeued === 1 ? "" : "s"} queued, ` +
        `${res.skipped} skipped.`
      );
      onDone?.(res);
    } catch (e) {
      setError((e && e.message) || "Could not start the re-run.");
    } finally {
      setBusy(false);
    }
  }

  const n = batch?.rerun_count || 0;
  const willRun = preview?.will_run || 0;

  return (
    <>
      <Button
        variant={variant} size={size}
        onClick={() => setOpen(true)}
        disabled={!terminal}
        title={terminal
          ? "Re-run only the cases a developer fixed plus the ones still failing"
          : "Wait for this batch to finish before re-running"}
      >
        <RotateCw size={14} /> Re-run{n > 0 ? ` ×${n}` : ""}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Re-run this batch"
        size="lg"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
              <b className="font-semibold text-slate-700 dark:text-slate-200">{willRun}</b> case{willRun === 1 ? "" : "s"}
              {/* The PC picker is only rendered when there is something to run, so
                  the count would otherwise refer to a control that isn't on screen. */}
              {withPicker && willRun > 0 && (
                <>
                  {" · "}<b className="font-semibold text-slate-700 dark:text-slate-200">{pickedOnline.length}</b> PC{pickedOnline.length === 1 ? "" : "s"}
                </>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={confirm} disabled={!canRun}>
                {busy
                  ? <><Loader2 size={15} className="animate-spin" /> Starting…</>
                  : <><RotateCw size={15} /> Re-run {willRun} case{willRun === 1 ? "" : "s"}</>}
              </Button>
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : error && !preview ? (
          <p className="flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {error}
          </p>
        ) : preview ? (
          <div className="space-y-4">
            {withPicker && (
              <input
                aria-label="Batch name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Name this re-run" className={FIELD}
              />
            )}

            {/* What re-runs is decided by the results, not by ticking rows — so this
                panel takes the slot the first-run modal gives its case filters. */}
            <div className="space-y-2.5 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
              <div className="grid gap-1.5">
                <Row Icon={Clock} tone="text-brand-600 dark:text-brand-300"
                  label="Developer marked fixed — verify" value={preview.dev_fixed_to_verify} />
                <Row Icon={XCircle} tone="text-rose-600 dark:text-rose-400"
                  label="Still failing — try again" value={preview.still_failing} />
                <Row Icon={CheckCircle2} tone="text-emerald-600 dark:text-emerald-400"
                  label="Already passed — skipped" value={preview.skipped_passed} muted />
                <Row Icon={SkipForward} tone="text-amber-600 dark:text-amber-400"
                  label="Blocked / manual — skipped" value={preview.skipped_blocked} muted />
              </div>

              <div className="flex items-center justify-between rounded-xl bg-white px-3.5 py-2.5 dark:bg-white/5">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Will run</span>
                <span className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                  {preview.will_run}
                  <span className="ml-1 text-sm font-normal text-slate-400">
                    of {preview.will_run + preview.will_skip}
                  </span>
                </span>
              </div>

              <p className="text-[0.6875rem] text-slate-400">
                Only what actually needs testing runs again — everything that already passed is
                left alone, which is where the time and cost saving comes from.
              </p>
            </div>

            {!preview.will_run && (
              <p className="flex items-start gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                Nothing to re-run — every case passed and no developer has marked a fix yet.
              </p>
            )}

            {/* PC picker — only in the Exe flow (devicesApi provided). Same header,
                list and busy treatment as the first-run modal. */}
            {withPicker && preview.will_run > 0 && (
              <div>
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <p className="flex items-baseline gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Online PCs
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                      {pickedOnline.length} selected
                    </span>
                    {busyCount > 0 && (
                      <span className="font-normal text-slate-400">
                        {freeDevices.length} free · {busyCount} busy
                      </span>
                    )}
                  </p>
                  {onlineDevices.length > 0 && (
                    <button type="button" className="shrink-0 text-xs font-medium text-brand-500 hover:underline"
                      onClick={() => setPicked(allDevices ? new Set() : new Set(onlineDevices.map((d) => d.id)))}>
                      {allDevices ? "Clear all" : "Select all"}
                    </button>
                  )}
                </div>
                {noOnline ? (
                  <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400 dark:border-white/10">
                    No online agents. Open the agent on a PC first.
                  </p>
                ) : (
                  <div className="max-h-32 space-y-1 overflow-auto rounded-xl border border-slate-200 p-1.5 dark:border-white/10">
                    {onlineDevices.map((d) => {
                      const on = picked.has(d.id);
                      return (
                        <label key={d.id}
                          className={cn("flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm",
                            on ? "bg-brand-50 dark:bg-brand-500/10" : "hover:bg-slate-50 dark:hover:bg-white/5")}>
                          <input type="checkbox" checked={on} onChange={() => toggle(d.id)}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                          <Server size={14} className={isBusy(d) ? "text-amber-500" : "text-emerald-500"} />
                          <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{devName(d)}</span>
                          {isBusy(d) && (
                            <span
                              className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                              title="Running another batch. Your re-run would start on this PC once that one finishes."
                            >
                              busy
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
                {offlineOriginals > 0 && !noOnline && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-[0.6875rem] text-amber-600 dark:text-amber-300">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    {offlineOriginals} PC{offlineOriginals === 1 ? "" : "s"} that ran this batch {offlineOriginals === 1 ? "is" : "are"} offline —
                    the re-run goes to the machines ticked above instead.
                  </p>
                )}
              </div>
            )}

            {preview.will_run > 0 && (
              <p className="text-[0.6875rem] text-slate-400">
                Cases are shared out across the chosen PCs automatically — a faster PC simply runs
                more. This will be re-run <strong>#{(batch?.rerun_count || 0) + 1}</strong> for this batch.
              </p>
            )}

            {error && (
              <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300">
                <AlertTriangle size={14} /> {error}
              </p>
            )}
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function Row({ Icon, tone, label, value, muted }) {
  return (
    <div className={
      "flex items-center justify-between rounded-xl border px-3 py-2 " +
      (muted
        ? "border-slate-100 bg-white/60 dark:border-white/5 dark:bg-white/5"
        : "border-slate-200 bg-white dark:border-white/10 dark:bg-white/5")
    }>
      <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
        <Icon size={14} className={tone} /> {label}
      </span>
      <span className={"text-sm font-bold tabular-nums " + (muted ? "text-slate-400" : "text-slate-900 dark:text-white")}>
        {value || 0}
      </span>
    </div>
  );
}
